// src/admin/screens/kyc/OcrCompare.tsx
// Side-by-side: what the borrower typed during KYC (profile row) vs what
// the OCR provider read off the NRC photo (ocr_result_jsonb). A check
// icon next to each row lets the reviewer spot-verify in seconds; the
// match function is case- and whitespace-insensitive so trivial typing
// differences don't trip a warning.
//
// The component is intentionally schema-lax on the OCR side: the provider
// (Smile ID / VOVE ID — still a BLOCKING decision per CLAUDE.md) may
// label fields slightly differently. We accept snake_case variants for
// the common four fields and leave unknown fields unrendered.
import Card from '@admin/components/Card';
import { Check, X } from 'lucide-react';

export interface OcrCompareProfile {
  display_name: string | null;
  full_name: string | null;
  date_of_birth: string | null;
  gender: string | null;
  nrc_number: string | null;
}

const normalize = (s: string | null | undefined): string =>
  (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function match(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return false;
  return na === nb;
}

function pickOcr(ocr: Record<string, unknown> | null, keys: string[]): string | null {
  if (!ocr) return null;
  for (const k of keys) {
    const v = ocr[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

function Row({
  label,
  profile,
  ocr,
}: {
  label: string;
  profile: string | null;
  ocr: string | null;
}) {
  const ok = match(profile, ocr);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '140px 1fr 1fr 24px',
        gap: 8,
        padding: '8px 0',
        borderBottom: '1px solid var(--c-border-lt)',
        alignItems: 'center',
      }}
    >
      <div className="small" style={{ color: 'var(--c-text-muted)' }}>
        {label}
      </div>
      <div>{profile ?? <span style={{ color: 'var(--c-text-muted)' }}>—</span>}</div>
      <div>{ocr ?? <span style={{ color: 'var(--c-text-muted)' }}>—</span>}</div>
      <div aria-label={ok ? 'match' : 'mismatch'}>
        {ok ? (
          <Check size={16} color="var(--c-success)" />
        ) : (
          <X size={16} color="var(--c-danger)" />
        )}
      </div>
    </div>
  );
}

interface Props {
  profile: OcrCompareProfile;
  ocr: Record<string, unknown> | null;
}

export default function OcrCompare({ profile, ocr }: Props) {
  const o = ocr ?? {};
  const profileName = profile.display_name ?? profile.full_name;
  return (
    <Card title="OCR comparison">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr 1fr 24px',
          gap: 8,
          fontWeight: 600,
          borderBottom: '1px solid var(--c-border)',
          paddingBottom: 6,
        }}
      >
        <div></div>
        <div>Profile</div>
        <div>OCR</div>
        <div></div>
      </div>
      <Row label="Full name" profile={profileName} ocr={pickOcr(o, ['full_name', 'name'])} />
      <Row label="NRC #" profile={profile.nrc_number} ocr={pickOcr(o, ['nrc_number', 'nrc'])} />
      <Row
        label="Date of birth"
        profile={profile.date_of_birth}
        ocr={pickOcr(o, ['date_of_birth', 'dob'])}
      />
      <Row label="Gender" profile={profile.gender} ocr={pickOcr(o, ['gender', 'sex'])} />
    </Card>
  );
}
